import { JSX } from 'react';
import { create } from 'zustand';

enum Theme {
  LIGHT = 'virtus',
  DARK = 'virtus-dark',
  ADVENT = 'virtus-advent',
}

const THEME_KEY = 'virtus.user-theme';
const BROWSER_THEME = window.matchMedia('(prefers-color-scheme: dark)').matches ? Theme.DARK : Theme.LIGHT;

export interface AppState {
  loading?: boolean;
  setLoading: (loading: boolean) => void;

  title?: string;
  setTitle: (title: string) => void;

  titleSize?: string;
  setTitleSize: (size: string) => void;

  backButton?: boolean;
  setBackButton: (show: boolean) => void;

  onBack?: () => void;
  setOnBack: (callback?: () => void) => void;

  hideFooter?: boolean;
  setHideFooter: (hide: boolean) => void;

  activeMenu?: string;
  setActiveMenu: (menu: string) => void;

  hiddenMenus?: string[];
  setHiddenMenus: (menus: string[]) => void;

  news: { [page: string]: number | undefined };
  setNews: (page: string, count: number) => void;

  headerComponent?: JSX.Element;
  setHeaderComponent: (component: JSX.Element) => void;

  lastComponentStack?: string;
  setLastComponentStack: (stack: string | undefined) => void;

  isDark: boolean;
  isThemeForced: boolean;
  theme: string;
  toggleTheme: () => void;

  updateLayout: (props: Partial<AppState>) => void;
}

export const useAppStore = create<AppState>((set) => {
  const storedTheme = localStorage.getItem(THEME_KEY);
  const forceTheme =
    new Date() >= new Date('2025-11-30T00:00:00Z') && new Date() <= new Date('2025-12-24T11:00:00Z')
      ? Theme.ADVENT
      : undefined;
  const initialTheme = forceTheme ?? storedTheme ?? BROWSER_THEME;

  return {
    loading: false,
    setLoading: (loading) => set({ loading }),

    title: undefined,
    setTitle: (title) => set({ title }),

    titleSize: undefined,
    setTitleSize: (size) => set({ titleSize: size }),

    backButton: false,
    setBackButton: (show) => set({ backButton: show }),

    onBack: undefined,
    setOnBack: (callback) => set({ onBack: callback }),

    hideFooter: false,
    setHideFooter: (hide) => set({ hideFooter: hide }),

    activeMenu: undefined,
    setActiveMenu: (menu) => set({ activeMenu: menu }),

    hiddenMenus: undefined,
    setHiddenMenus: (menus) => set({ hiddenMenus: menus }),

    news: {},
    setNews: (page: string, count: number) => set((existing) => ({ news: { ...existing.news, [page]: count } })),

    headerComponent: undefined,
    setHeaderComponent: (component: JSX.Element) => set({ headerComponent: component }),

    lastComponentStack: undefined,
    setLastComponentStack: (stack) => set({ lastComponentStack: stack }),

    isDark: initialTheme !== Theme.LIGHT,
    isThemeForced: !!forceTheme,
    theme: initialTheme,
    toggleTheme: () =>
      set((state) => {
        if (forceTheme) return {};

        const newTheme = state.isDark ? Theme.LIGHT : Theme.DARK;
        localStorage.setItem(THEME_KEY, newTheme);
        return { isDark: newTheme !== Theme.LIGHT, theme: newTheme };
      }),

    updateLayout: (props) => set((state) => ({ ...state, ...props })),
  };
});
